/**
 * BlendSafe FAQ — single source of truth.
 *
 * Used by:
 *   • Homepage FAQ accordion (FaqSection.jsx)
 *   • /faq index page (FaqIndex.jsx)
 *   • /faq/:slug detail pages (FaqDetail.jsx)
 *   • FAQPage JSON-LD on homepage and detail pages
 *
 * Copy is medically cautious by design — never makes absolute safety claims.
 * Reorder entries with care: changing slugs breaks live URLs / sitemap.
 */

export const FAQ_CATEGORIES = [
  { id: "about", label: "About BlendSafe" },
  { id: "safety", label: "Safety & Results" },
  { id: "meds", label: "Medications & Ingredients" },
  { id: "trust", label: "Accuracy & Trust" },
  { id: "founding", label: "Founding Access" },
];

export const FAQS = [
  {
    slug: "is-blendsafe-free",
    category: "about",
    question: "Is BlendSafe free to use?",
    answer: [
      "Yes. BlendSafe is free to start. Users can check skincare products, ingredients, and medication combinations without creating an account.",
      "Some advanced features — such as saved routines, exports, expanded history, or future premium tools — may require a subscription after launch.",
    ],
    metaTitle: "Is BlendSafe free? | BlendSafe FAQ",
    metaDescription:
      "Yes, BlendSafe is free to start. Check skincare products, ingredients, and medication interactions without an account.",
  },
  {
    slug: "what-does-avoid-mean",
    category: "safety",
    question: "What does AVOID mean?",
    answer: [
      "AVOID means BlendSafe identified a stronger interaction concern between a medication and one or more skincare ingredients.",
      "This may involve increased irritation risk, barrier stress, photosensitivity, duplicate retinoids, or ingredient combinations commonly discouraged together.",
      "It does not necessarily mean the product itself is “bad” — it means the combination deserves extra caution.",
    ],
    metaTitle: "What does AVOID mean in BlendSafe? | FAQ",
    metaDescription:
      "AVOID in BlendSafe flags a stronger interaction concern between a medication and one or more skincare ingredients — extra caution is warranted.",
  },
  {
    slug: "blendsafe-and-accutane",
    category: "meds",
    question: "Can I use BlendSafe while taking Accutane?",
    answer: [
      "Yes. BlendSafe is especially useful for users taking medications such as isotretinoin (Accutane), tretinoin, adapalene, doxycycline, spironolactone, and other treatments commonly associated with dryness, irritation, or sun sensitivity.",
    ],
    metaTitle: "Using BlendSafe with Accutane (isotretinoin) | FAQ",
    metaDescription:
      "BlendSafe is especially useful while taking Accutane, tretinoin, doxycycline, spironolactone and other medications linked to skin sensitivity.",
  },
  {
    slug: "does-blendsafe-replace-dermatologist",
    category: "trust",
    question: "Does BlendSafe replace a dermatologist?",
    answer: [
      "No. BlendSafe is an educational skincare safety tool and does not replace professional medical advice, diagnosis, or treatment.",
      "Users should always follow the guidance of their dermatologist, physician, or pharmacist — especially for prescription medications or active skin conditions.",
    ],
    metaTitle: "Does BlendSafe replace a dermatologist? | FAQ",
    metaDescription:
      "No. BlendSafe is an educational safety tool. Always follow the guidance of your dermatologist, physician, or pharmacist.",
  },
  {
    slug: "product-not-in-database",
    category: "about",
    question: "What if my product is not in the database?",
    answer: [
      "Users may still:",
      "• paste ingredient lists manually",
      "• search by ingredient",
      "• try alternative product naming",
      "• use barcode lookup when available",
      "• use future ingredient/photo extraction tools as they expand",
      "BlendSafe’s product catalog continues to grow over time.",
    ],
    metaTitle: "My product isn’t in BlendSafe — what now? | FAQ",
    metaDescription:
      "If a product isn’t found, paste ingredients, search by ingredient, try alternative names, or use barcode lookup. The catalog keeps growing.",
  },
  {
    slug: "how-accurate-are-results",
    category: "trust",
    question: "How accurate are BlendSafe results?",
    answer: [
      "BlendSafe uses ingredient interaction logic, medication safety guidance, FDA labeling references, and dermatology-related research sources to identify potential skincare concerns.",
      "SAFE means no known flagged concern was identified in the available data — not that every individual reaction is guaranteed.",
      "Personal skin sensitivity, allergies, dosage, and routine structure still matter.",
    ],
    metaTitle: "How accurate are BlendSafe results? | FAQ",
    metaDescription:
      "BlendSafe references ingredient logic, FDA labels, and dermatology sources. SAFE means no flagged concern was found — personal sensitivity still matters.",
  },
  {
    slug: "supported-medications",
    category: "meds",
    question: "What medications does BlendSafe support?",
    answer: [
      "BlendSafe currently supports a growing list of medications commonly associated with skincare concerns, including retinoids, antibiotics, hormonal medications, topical prescriptions, and other treatments linked to irritation or photosensitivity.",
    ],
    metaTitle: "What medications does BlendSafe support? | FAQ",
    metaDescription:
      "BlendSafe supports a growing list of medications: retinoids, antibiotics, hormonal medications, topical prescriptions, and others linked to skin sensitivity.",
  },
  {
    slug: "supplements-check",
    category: "meds",
    question: "Can BlendSafe check supplements too?",
    answer: [
      "Yes. Some supplements may also affect skincare sensitivity or interact with active ingredients.",
      "BlendSafe may flag certain supplement-related concerns when relevant information is available.",
    ],
    metaTitle: "Can BlendSafe check supplements? | FAQ",
    metaDescription:
      "Yes. Some supplements affect skincare sensitivity or interact with actives — BlendSafe may flag supplement-related concerns when data is available.",
  },
  {
    slug: "data-sources",
    category: "trust",
    question: "Where does BlendSafe interaction data come from?",
    answer: [
      "BlendSafe references publicly available dermatology guidance, FDA label information, ingredient safety research, and medication-related skincare literature to help identify potential concerns.",
      "The platform is designed as a safety-awareness tool, not a diagnostic system.",
    ],
    metaTitle: "Where does BlendSafe data come from? | FAQ",
    metaDescription:
      "BlendSafe references publicly available dermatology guidance, FDA labels, ingredient safety research, and medication-related skincare literature.",
  },
  {
    slug: "what-is-founding-member",
    category: "founding",
    question: "What is a Founding Member?",
    answer: [
      "Founding Members are early BlendSafe supporters who join during the initial access phase.",
      "Founding access may include:",
      "• early feature access",
      "• priority product updates",
      "• future premium perks",
      "• early community participation opportunities",
      "Availability may become limited as BlendSafe grows.",
    ],
    metaTitle: "What is a BlendSafe Founding Member? | FAQ",
    metaDescription:
      "Founding Members are early BlendSafe supporters who get early feature access, priority updates, future premium perks, and community opportunities.",
  },
];

/** Lookup helper — returns the FAQ matching a slug, or undefined. */
export function findFaqBySlug(slug) {
  return FAQS.find((f) => f.slug === slug);
}

/** Returns up to N related FAQs — prefers same category, then fills from
 *  other categories so every detail page has at least a few related links. */
export function relatedFaqs(slug, limit = 3) {
  const me = findFaqBySlug(slug);
  if (!me) return [];
  const sameCat = FAQS.filter((f) => f.slug !== slug && f.category === me.category);
  if (sameCat.length >= limit) return sameCat.slice(0, limit);
  const fillers = FAQS.filter(
    (f) => f.slug !== slug && f.category !== me.category,
  ).slice(0, limit - sameCat.length);
  return [...sameCat, ...fillers];
}

/** Plain-text answer for JSON-LD (joined with spaces, bullets preserved). */
export function answerPlainText(faq) {
  return (faq.answer || []).join(" ").trim();
}

/** Build the FAQPage JSON-LD object for an array of FAQs. */
export function buildFaqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answerPlainText(f),
      },
    })),
  };
}
